import type { TaxRule } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ChipFilter,
  Container,
  Dialog,
  DialogClose,
  DialogContent,
  EmptyState,
  ErrorState,
  IconButton,
  NoPermissionState,
  Notice,
  PageHeader,
  RowActions,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Lightbulb, Pencil, Percent, Plus, Power, PowerOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can, useCan } from '@/features/auth/session';
import {
  TaxRuleFormDrawer,
  VAT_EXAMPLE_PRESET,
  type TaxRulePreset,
} from '@/features/tax/TaxRuleFormDrawer';
import { taxRuleKeys, taxRulesQueryOptions, updateTaxRule } from '@/features/tax/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatDateShort } from '@/lib/format';

export const Route = createFileRoute('/_app/admin/taxes')({ component: TaxesPage });

type StatusFilter = 'active' | 'inactive' | 'all';
const STATUS_FILTERS: StatusFilter[] = ['active', 'inactive', 'all'];

/** Taux affiché avec au plus deux décimales (5 %, 5,5 %, 18 %). */
function formatRate(rate: number, locale: string): string {
  return new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'fr-FR', {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(rate);
}

function TaxesPage() {
  const { t, i18n } = useTranslation(['taxes', 'common', 'errors']);
  const locale = i18n.language;
  const queryClient = useQueryClient();
  const canRead = useCan('tax.read');
  const canManage = useCan('tax.manage');

  const [status, setStatus] = useState<StatusFilter>('active');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRuleId, setDrawerRuleId] = useState<string | undefined>(undefined);
  const [preset, setPreset] = useState<TaxRulePreset | undefined>(undefined);
  const [toggling, setToggling] = useState<TaxRule | undefined>(undefined);

  const rulesQuery = useQuery({
    ...taxRulesQueryOptions({ includeInactive: true }),
    enabled: canRead,
  });

  const toggleActive = useMutation({
    mutationFn: (rule: TaxRule) =>
      updateTaxRule(rule.id, { isActive: !rule.isActive, version: rule.version }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: taxRuleKeys.all });
      toast.success(
        saved.isActive
          ? t('taxes:toasts.activated', { code: saved.code })
          : t('taxes:toasts.deactivated', { code: saved.code }),
      );
    },
    onError: async (error) => {
      toast.error(errorMessage(error, t));
      // Verrou optimiste : on recharge la liste pour repartir de la dernière version.
      await queryClient.invalidateQueries({ queryKey: taxRuleKeys.all });
    },
    onSettled: () => setToggling(undefined),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const allRules = rulesQuery.data ?? [];
  const rules = allRules.filter((rule) =>
    status === 'all' ? true : status === 'active' ? rule.isActive : !rule.isActive,
  );
  // La règle du tiroir est relue dans la liste : après un rechargement, le
  // formulaire repart de la dernière version (verrou optimiste).
  const drawerRule = drawerRuleId ? allRules.find((rule) => rule.id === drawerRuleId) : undefined;

  const openCreate = (values?: TaxRulePreset) => {
    setDrawerRuleId(undefined);
    setPreset(values);
    setDrawerOpen(true);
  };
  const openEdit = (rule: TaxRule) => {
    setDrawerRuleId(rule.id);
    setPreset(undefined);
    setDrawerOpen(true);
  };

  const validityOf = (rule: TaxRule): string => {
    if (rule.validFrom && rule.validTo) {
      return t('taxes:validity.between', {
        from: formatDateShort(rule.validFrom, locale),
        to: formatDateShort(rule.validTo, locale),
      });
    }
    if (rule.validFrom) {
      return t('taxes:validity.from', { date: formatDateShort(rule.validFrom, locale) });
    }
    if (rule.validTo) {
      return t('taxes:validity.until', { date: formatDateShort(rule.validTo, locale) });
    }
    return t('taxes:validity.open');
  };

  return (
    <Container>
      <PageHeader
        title={t('taxes:title')}
        subtitle={t('taxes:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('taxes:title') },
        ]}
        actions={
          <Can permission="tax.manage">
            <Button variant="primary" onClick={() => openCreate(undefined)}>
              <Plus className="ico" aria-hidden="true" />
              {t('taxes:create')}
            </Button>
          </Can>
        }
      />

      <div className="stack">
        <Notice
          tone="violet"
          icon={<Lightbulb className="ico ico-16" aria-hidden="true" />}
          title={t('taxes:help.title')}
          actions={
            canManage ? (
              <Button variant="secondary" size="sm" onClick={() => openCreate(VAT_EXAMPLE_PRESET)}>
                {t('taxes:help.prefill')}
              </Button>
            ) : undefined
          }
        >
          {t('taxes:help.body')}
        </Notice>

        <Card>
          <CardHeader
            title={t('taxes:title')}
            icon={<Percent className="ico ico-16" aria-hidden="true" />}
            actions={<span className="muted">{t('taxes:count', { count: rules.length })}</span>}
          />

          <Toolbar>
            {STATUS_FILTERS.map((value) => (
              <ChipFilter key={value} active={status === value} onClick={() => setStatus(value)}>
                {t(`taxes:filters.${value}`)}
              </ChipFilter>
            ))}
          </Toolbar>

          {rulesQuery.isPending ? (
            <TableSkeleton rows={3} columns={7} label={t('common:states.loading')} />
          ) : rulesQuery.isError ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(rulesQuery.error, t)}
              traceId={errorTraceId(rulesQuery.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void rulesQuery.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : rules.length === 0 ? (
            <EmptyState
              icon={<Percent className="ico ico-24" aria-hidden="true" />}
              title={allRules.length === 0 ? t('taxes:emptyTitle') : t('taxes:emptyFilteredTitle')}
              description={
                allRules.length === 0 ? t('taxes:emptyBody') : t('taxes:emptyFilteredBody')
              }
              action={
                allRules.length > 0 ? (
                  <Button variant="secondary" onClick={() => setStatus('all')}>
                    {t('taxes:filters.all')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('taxes:columns.rule')}</Th>
                    <Th>{t('taxes:columns.kind')}</Th>
                    <Th align="right">{t('taxes:columns.rate')}</Th>
                    <Th>{t('taxes:columns.base')}</Th>
                    <Th>{t('taxes:columns.appliesTo')}</Th>
                    <Th>{t('taxes:columns.validity')}</Th>
                    <Th>{t('taxes:columns.status')}</Th>
                    {canManage ? (
                      <Th align="right">
                        <span className="sr-only">{t('taxes:columns.actions')}</span>
                      </Th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <Tr key={rule.id}>
                      <Td strong>
                        {rule.name}
                        <span className="cell-user__sub cell-id">{rule.code}</span>
                      </Td>
                      <Td>
                        <div className="row row--wrap">
                          <Badge tone={rule.kind === 'TAX' ? 'violet' : 'blue'}>
                            {t(`taxes:kind.${rule.kind}`)}
                          </Badge>
                          {rule.isInclusive ? (
                            <Badge tone="neutral">{t('taxes:flags.inclusive')}</Badge>
                          ) : null}
                          {rule.isOptional ? (
                            <Badge tone="cyan">{t('taxes:flags.optional')}</Badge>
                          ) : null}
                        </div>
                      </Td>
                      <Td align="right" numeric>
                        {formatRate(rule.rate, locale)}
                      </Td>
                      <Td>{t(`taxes:base.${rule.base}`)}</Td>
                      <Td>{t(`taxes:appliesTo.${rule.appliesTo}`)}</Td>
                      <Td>{validityOf(rule)}</Td>
                      <Td>
                        <Badge tone={rule.isActive ? 'green' : 'neutral'} dot>
                          {rule.isActive ? t('taxes:status.active') : t('taxes:status.inactive')}
                        </Badge>
                      </Td>
                      {canManage ? (
                        <Td align="right">
                          <RowActions>
                            <IconButton
                              label={`${t('taxes:actions.edit')} — ${rule.name}`}
                              onClick={() => openEdit(rule)}
                            >
                              <Pencil className="ico ico-16" aria-hidden="true" />
                            </IconButton>
                            <IconButton
                              label={`${rule.isActive ? t('taxes:actions.deactivate') : t('taxes:actions.activate')} — ${rule.name}`}
                              onClick={() => setToggling(rule)}
                            >
                              {rule.isActive ? (
                                <PowerOff className="ico ico-16" aria-hidden="true" />
                              ) : (
                                <Power className="ico ico-16" aria-hidden="true" />
                              )}
                            </IconButton>
                          </RowActions>
                        </Td>
                      ) : null}
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Card>

        <Notice tone="info">
          <p style={{ margin: 0 }}>{t('taxes:kindHelp')}</p>
          <p style={{ margin: '4px 0 0' }}>{t('taxes:noDeleteHelp')}</p>
        </Notice>
      </div>

      <TaxRuleFormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        rule={drawerRule}
        preset={preset}
      />

      <Dialog
        open={toggling !== undefined}
        onOpenChange={(open) => {
          if (!open && !toggleActive.isPending) setToggling(undefined);
        }}
      >
        {toggling ? (
          <DialogContent
            title={
              toggling.isActive
                ? t('taxes:confirm.deactivateTitle', { name: toggling.name })
                : t('taxes:confirm.activateTitle', { name: toggling.name })
            }
            description={
              toggling.isActive
                ? t('taxes:confirm.deactivateBody')
                : t('taxes:confirm.activateBody')
            }
            closeLabel={t('common:actions.close')}
            icon={
              toggling.isActive ? (
                <PowerOff className="ico ico-16" aria-hidden="true" />
              ) : (
                <Power className="ico ico-16" aria-hidden="true" />
              )
            }
            iconTone={toggling.isActive ? 'amber' : 'green'}
            footer={
              <>
                <DialogClose asChild>
                  <Button variant="ghost">{t('common:actions.cancel')}</Button>
                </DialogClose>
                <Button
                  variant={toggling.isActive ? 'danger' : 'primary'}
                  loading={toggleActive.isPending}
                  onClick={() => toggleActive.mutate(toggling)}
                >
                  {toggling.isActive
                    ? t('taxes:confirm.deactivateConfirm')
                    : t('taxes:confirm.activateConfirm')}
                </Button>
              </>
            }
          />
        ) : null}
      </Dialog>
    </Container>
  );
}
